import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { toast } from "sonner";
import BirthOrAgeInput from "@/components/BirthOrAgeInput";
import BreedAutocomplete from "@/components/BreedAutocomplete";
import { sanitizePhone, isValidEmail } from "@/lib/patientUtils";

export default function NewPatientDialog({ open, onOpenChange, onCreated }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const [speciesList, setSpeciesList] = useState([]);
  const [cnpMatches, setCnpMatches] = useState([]);
  const [cnpAcknowledged, setCnpAcknowledged] = useState(false);
  const cnpTimerRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    species: "dog",
    breed: "",
    sex: "",
    birth_date: "",
    birth_date_approximate: false,
    neutered: false,
    owner_full_name: "",
    owner_phone: "",
    owner_email: "",
    owner_address: "",
    owner_cnp: "",
  });

  useEffect(() => {
    if (open) {
      api.get("/species").then((r) => setSpeciesList(r.data)).catch(() => {});
    } else {
      setCnpMatches([]);
      setCnpAcknowledged(false);
    }
  }, [open]);

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  // Debounced CNP duplicate check
  useEffect(() => {
    if (cnpTimerRef.current) clearTimeout(cnpTimerRef.current);
    const cnp = form.owner_cnp?.trim() || "";
    if (cnp.length < 6) {
      setCnpMatches([]);
      setCnpAcknowledged(false);
      return;
    }
    cnpTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/patients/check-cnp`, { params: { cnp } });
        setCnpMatches(data?.matches || []);
        setCnpAcknowledged(false);
      } catch {
        setCnpMatches([]);
      }
    }, 400);
    return () => cnpTimerRef.current && clearTimeout(cnpTimerRef.current);
  }, [form.owner_cnp]);

  async function submit(e) {
    e.preventDefault();
    if (form.owner_email && !isValidEmail(form.owner_email)) {
      toast.error(t("invalidEmail"));
      return;
    }
    if (cnpMatches.length > 0 && !cnpAcknowledged) {
      toast.warning(t("cnpExistsWarning"));
      return;
    }
    setBusy(true);
    try {
      const body = {
        name: form.name,
        species: form.species,
        breed: form.breed,
        sex: form.sex,
        birth_date: form.birth_date || null,
        birth_date_approximate: !!form.birth_date_approximate,
        neutered: !!form.neutered,
        owner: {
          full_name: form.owner_full_name,
          phone: form.owner_phone,
          email: form.owner_email,
          address: form.owner_address,
          cnp: form.owner_cnp,
        },
      };
      const { data } = await api.post("/patients", body);
      toast.success(`${data.name} added`);
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  const showCnpWarning = cnpMatches.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{t("newPatient")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="pf-label mb-1 block">{t("name")}</label>
              <input data-testid="np-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="pf-input" />
            </div>
            <div>
              <label className="pf-label mb-1 block">{t("species")}</label>
              <select data-testid="np-species" value={form.species} onChange={(e) => set("species", e.target.value)} className="pf-input">
                {speciesList.length === 0 ? (
                  <>
                    <option value="dog">{t("dog")}</option>
                    <option value="cat">{t("cat")}</option>
                  </>
                ) : (
                  speciesList.map((s) => (
                    <option key={s.id} value={s.name}>
                      {s.name === "dog" ? t("dog") : s.name === "cat" ? t("cat") : s.name}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div>
              <label className="pf-label mb-1 block">{t("breed")}</label>
              <BreedAutocomplete
                species={form.species}
                value={form.breed}
                onChange={(v) => set("breed", v)}
              />
            </div>
            <div>
              <label className="pf-label mb-1 block">{t("sex")}</label>
              <select value={form.sex} onChange={(e) => set("sex", e.target.value)} className="pf-input">
                <option value="">—</option>
                <option value="male">{t("male")}</option>
                <option value="female">{t("female")}</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <BirthOrAgeInput
                testIdPrefix="np-birth"
                value={form.birth_date}
                approximate={!!form.birth_date_approximate}
                onChange={({ birth_date, birth_date_approximate }) =>
                  setForm((f) => ({ ...f, birth_date, birth_date_approximate }))
                }
              />
            </div>
            <div className="sm:col-span-2 flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  data-testid="np-neutered"
                  type="checkbox"
                  checked={form.neutered}
                  onChange={(e) => set("neutered", e.target.checked)}
                />
                {t("neutered")}
              </label>
            </div>
          </div>
          <div className="pt-3 border-t border-pf-border">
            <div className="pf-label mb-2">{t("owner")}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="pf-label mb-1 block">{t("fullName")}</label>
                <input data-testid="np-owner-name" required value={form.owner_full_name} onChange={(e) => set("owner_full_name", e.target.value)} className="pf-input" />
              </div>
              <div>
                <label className="pf-label mb-1 block">{t("phone")}</label>
                <input
                  data-testid="np-owner-phone"
                  inputMode="tel"
                  value={form.owner_phone}
                  onChange={(e) => set("owner_phone", sanitizePhone(e.target.value))}
                  className="pf-input"
                  placeholder="+40 …"
                />
              </div>
              <div>
                <label className="pf-label mb-1 block">{t("email")}</label>
                <input
                  data-testid="np-owner-email"
                  type="email"
                  value={form.owner_email}
                  onChange={(e) => set("owner_email", e.target.value)}
                  className="pf-input"
                />
              </div>
              <div>
                <label className="pf-label mb-1 block">{t("cnp")}</label>
                <input
                  data-testid="np-owner-cnp"
                  value={form.owner_cnp}
                  onChange={(e) => set("owner_cnp", e.target.value.replace(/[^\d]/g, ""))}
                  maxLength={13}
                  className={`pf-input ${showCnpWarning ? "border-amber-500 focus:border-amber-500" : ""}`}
                  placeholder="1234567890123"
                />
                {showCnpWarning && (
                  <div
                    data-testid="cnp-warning"
                    className="mt-2 rounded-md border border-amber-400 bg-amber-50 text-amber-900 p-2 text-xs space-y-1"
                  >
                    <div className="font-medium">{t("cnpExistsWarning")}</div>
                    <div className="text-amber-800">{t("cnpExistsHint")}</div>
                    <div className="pt-1">
                      <div className="text-[11px] uppercase tracking-wide text-amber-700 mb-1">
                        {t("existingPatients")}:
                      </div>
                      <ul className="list-disc list-inside space-y-0.5">
                        {cnpMatches.slice(0, 5).map((m) => (
                          <li key={m.id}>
                            <span className="font-medium">{m.patient_name}</span>
                            {m.owner_name ? ` — ${m.owner_name}` : ""}
                            {m.owner_phone ? ` (${m.owner_phone})` : ""}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <label className="flex items-center gap-2 pt-1">
                      <input
                        data-testid="cnp-ack"
                        type="checkbox"
                        checked={cnpAcknowledged}
                        onChange={(e) => setCnpAcknowledged(e.target.checked)}
                      />
                      <span>{t("keepAndSave")}</span>
                    </label>
                  </div>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="pf-label mb-1 block">{t("address")}</label>
                <input value={form.owner_address} onChange={(e) => set("owner_address", e.target.value)} className="pf-input" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="pf-btn-ghost">
              {t("cancel")}
            </button>
            <button
              data-testid="np-submit"
              type="submit"
              disabled={busy || (showCnpWarning && !cnpAcknowledged)}
              className="pf-btn-primary"
            >
              {busy ? t("saving") : t("createPatient")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
